import React from 'react';
import { Box } from '@mui/material';

const visuallyHiddenStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const normalizeCountryCode = (countryCode) => {
  if (!countryCode) {
    return '';
  }

  const normalizedCode = String(countryCode).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalizedCode) ? normalizedCode : '';
};

const isSvgUrl = (url) => {
  return url && typeof url === 'string' && (url.includes('flagcdn.com') || url.includes('.svg'));
};

const resolveCountryCode = (country) =>
  normalizeCountryCode(country?.isoCode) ||
  normalizeCountryCode(country?.iso_code) ||
  normalizeCountryCode(country?.code) ||
  '';

export function CountryFlag({ countryCode, flagUrl, alt, sx }) {
  // If flagUrl is provided and is an SVG URL, render it as an image
  if (isSvgUrl(flagUrl)) {
    return (
      <Box
        component="img"
        src={flagUrl}
        alt={alt || 'Country flag'}
        sx={{
          display: 'inline-block',
          height: '15px',
          width: 'auto',
          maxWidth: '20px',
          borderRadius: '2px',
          boxShadow: '0 0 0 1px rgba(28, 27, 31, 0.08)',
          flexShrink: 0,
          ...sx,
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  }

  // Fallback to flag icon class if countryCode is provided
  const normalizedCode = normalizeCountryCode(countryCode);

  if (!normalizedCode) {
    return null;
  }

  return (
    <Box
      component="span"
      role="img"
      aria-label={alt || `${normalizedCode} flag`}
      className={`fi fi-${normalizedCode.toLowerCase()}`}
      sx={{
        display: 'inline-block',
        width: '20px',
        height: '15px',
        borderRadius: '2px',
        boxShadow: '0 0 0 1px rgba(28, 27, 31, 0.08)',
        flexShrink: 0,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...sx,
      }}
    />
  );
}

export function CountryPhoneCodeOption({ country, fallbackLabel }) {
  const countryCode = resolveCountryCode(country);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
      }}
    >
      <CountryFlag
        countryCode={countryCode}
        flagUrl={country?.countryFlag || country?.flag_symbol || country?.flagSymbol}
        alt={`${country?.name || countryCode} flag`}
      />
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {country?.phoneCode || fallbackLabel || ''}
      </Box>
    </Box>
  );
}

export function CountryPhoneCodeMenuOption({ country, fallbackLabel }) {
  const countryCode = resolveCountryCode(country);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box component="span" sx={visuallyHiddenStyles}>
        {country?.name || ''}
      </Box>
      <CountryFlag
        countryCode={countryCode}
        flagUrl={country?.countryFlag || country?.flag_symbol || country?.flagSymbol}
        alt={`${country?.name || countryCode} flag`}
      />
      <Box
        component="span"
        sx={{
          whiteSpace: 'nowrap',
          fontWeight: 600,
        }}
      >
        {country?.phoneCode || fallbackLabel || ''}
      </Box>
      <Box
        component="span"
        sx={{
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {country?.name || ''}
      </Box>
    </Box>
  );
}
